/**
 * ItemGroupSlugEditModal — edit modal for Item Group storefront slug.
 *
 * Simplified version of Item's StorefrontFieldEditModal:
 *   - Slug field only (no catalog_only toggle, no description)
 *   - URL preview shows /collections/ path (not /product/)
 *   - Saves via usePatchSaveDoc
 */

import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";
import { usePatchSaveDoc } from "@/lib/hooks/usePatchSaveDoc";
import { useQueryClient } from "@tanstack/react-query";
import { slugify, displaySlug } from "../../item/widgets/storefrontUtils";

interface ItemGroupSlugEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSlug: string;
  storefrontBaseUrl: string;
  docName: string;
  onFieldChange: (fieldname: string, value: any) => void;
}

export function ItemGroupSlugEditModal({
  open,
  onOpenChange,
  currentSlug,
  storefrontBaseUrl,
  docName,
  onFieldChange,
}: ItemGroupSlugEditModalProps) {
  const [slugValue, setSlugValue] = useState(displaySlug(currentSlug));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const detailKey = ["Item Group", docName] as const;

  const saveMutation = usePatchSaveDoc({
    doctype: "Item Group",
    docname: docName,
    detailKey,
  });

  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setSlugValue(displaySlug(currentSlug));
      setSubmitError(null);
    }
    prevOpen.current = open;
  }, [open, currentSlug]);

  const handleSlugChange = (raw: string) => {
    setSlugValue(slugify(raw));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const slugChanged = slugValue !== currentSlug;

    if (!slugChanged) {
      onOpenChange(false);
      return;
    }

    setSubmitError(null);

    try {
      await saveMutation.mutateAsync({ storefront_slug: slugValue });
      onFieldChange("storefront_slug", slugValue);
      queryClient.invalidateQueries({ queryKey: [...detailKey] });
      onOpenChange(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to save changes";
      setSubmitError(msg);
    }
  };

  const slugPreview = `${storefrontBaseUrl}/collections/${displaySlug(slugValue) || "your-category-slug"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden max-w-[680px]">
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={saveMutation.isPending}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
          title="Save changes"
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">Save</span>
        </Button>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
            <DialogHeader className="px-10 pt-20 pb-4 text-center">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                Storefront URL
              </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                Customize the URL slug for this category
              </DialogDescription>
              {submitError && (
                <div className="mt-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                </div>
              )}
            </DialogHeader>

            <div className="pb-8 px-10 pt-2">
              <div className="py-2 space-y-5">
                <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                  <div className="px-5 py-4 space-y-3">
                    <div className="flex items-center justify-between min-h-[52px]">
                      <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                        URL Slug
                      </label>
                      <div className="flex-1 max-w-[60%] flex justify-end">
                        <Input
                          type="text"
                          value={slugValue}
                          onChange={(e) => handleSlugChange(e.target.value)}
                          onBlur={() => setSlugValue((v) => slugify(v))}
                          placeholder="my-category-name"
                          className="flex-1 text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground truncate">
                      {slugPreview}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="submit"
              disabled={saveMutation.isPending}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
